import type { OrbitJourney, OrbitOverview, OrbitPhaseState, PhaseScreen } from '../../types/aai';

/*
 * The journey: the phases, in order, one open at a time.
 *
 * The words are product copy and live only here, so a phase reads the same on
 * the owner's company page and on the partner's client page. Each phase says
 * what it is FOR, never who runs it: a client reading their own plan should not
 * find a Solution Partner in it. What each phase builds is listed because a
 * person about to spend an hour deserves to know what they get for it.
 *
 * The server stores only which phase is closed (orbit_phase). Everything else
 * on screen is read from the Room, so a phase cannot claim progress the maps
 * do not have.
 *
 * The ORDER and the words now come from the server: a partner sets their own
 * order, and a client can be prepared with their own arc. PHASES below is only
 * what is drawn before the journey has loaded. Each step names the intake it
 * opens (`screen`), and the Room opens it by that, never by its position.
 */

export interface PhaseCopy {
  n: number;
  key: string;
  /** Which intake this step opens. */
  screen: PhaseScreen;
  title: string;
  /** Why this phase exists, in one sentence. */
  purpose: string;
  /** What the company holds once it is done. */
  builds: string[];
  /** What finishing it opens. */
  unlocks: string;
  /** The primary action on the open phase. */
  action: string;
  /** Kept in the arc, off the screen: Run has nothing to open yet. */
  hidden?: boolean;
}

export const PHASES: PhaseCopy[] = [
  {
    n: 1,
    key: 'frame',
    screen: 'frame',
    title: 'Business intake',
    purpose: 'Turn Orbit\'s outside-in read into inside-out truth, so everything after it stands on solid ground.',
    builds: ['The corrected map', 'What Orbit suspects, ranked', 'The doors: people, documents, systems'],
    unlocks: 'Next: Department intake',
    action: 'Begin the session',
  },
  {
    n: 2,
    key: 'departments',
    screen: 'departments',
    title: 'Department intake',
    purpose: 'Learn how each department actually works: the real workflow, the numbers, where it strains.',
    builds: ['Department maps, made true', 'Workflow and handoffs', 'Baselines and friction'],
    unlocks: 'Next: Person intake',
    action: 'Open departments',
  },
  /* Team before tools.
     A department's tools are the tools its people use, so asking who is in it
     before asking what it runs on means the tools question is answered by the
     people who actually open them. It also lets the tool map be scoped to a
     department that already has a roster, rather than to an empty one. */
  {
    n: 3,
    key: 'team',
    screen: 'team',
    title: 'Person intake',
    purpose: 'Each person answers a few questions about their own work, so Orbit knows how they work before anything is built for them.',
    builds: ['Person maps', 'Readiness picture', 'Person-level wins'],
    unlocks: 'Next: Tool intake',
    action: 'Open the Person intake',
  },
  {
    n: 4,
    key: 'tools',
    screen: 'tools',
    title: 'Tool intake',
    purpose: 'Map the tools as they are truly used, connect them, and scope what agents may do.',
    builds: ['Tool map and usage depth', 'Access ledger', 'Overlap findings'],
    unlocks: 'Quick wins, priced',
    action: 'Connect the tools',
  },
  {
    n: 5,
    key: 'report',
    screen: 'report',
    title: 'The clarity report',
    purpose: 'See it whole: where the value is, what it is worth, and an honest verdict on what to do.',
    builds: ['Readiness scorecard', 'Opportunities, priced in ranges', '90-day roadmap and the first domain'],
    unlocks: 'Your sign-off. Nothing builds without it',
    action: 'Open the report',
  },
  {
    n: 6,
    key: 'run',
    screen: 'run',
    title: 'Run: agents work, people oversee',
    purpose: 'Put it to work. Agents do the day-to-day, your people oversee, and value is measured against your baselines.',
    builds: ['One domain, proven', 'Department agents at work', 'Live value against baselines'],
    unlocks: 'Orbit runs again · quarterly refresh',
    action: 'Open the run view',
    hidden: true,
  },
];

export interface PhaseRow extends PhaseCopy {
  state: OrbitPhaseState;
  doneBy: string;
  doneAt: string | null;
  /** What this phase has to show for itself so far, read from the Room. */
  progress: string;
}

/* The word under the percentage describes the maps, not the phases: a company
   can close a phase with cards still open, and two numbers that disagree on
   the same panel read as a bug. */
const COMPLETION_WORD: [number, string][] = [
  [90, 'Fully mapped'],
  [70, 'Nearly true'],
  [40, 'Taking shape'],
  [15, 'Early'],
  [0, 'Just started'],
];

/** How full the maps are, across every area Orbit tracks. Real coverage, not a
 *  count of phases: a company can close a phase with cards still open. */
export const completion = (overview: OrbitOverview | null): number => {
  if (!overview || overview.areas.length === 0) return 0;
  const total = overview.areas.reduce((sum, a) => sum + a.pct, 0);
  return Math.round(total / overview.areas.length);
};

/* An average can read high while one map is still empty. With any map thin,
   the word stops at 'Taking shape' rather than calling the picture nearly true. */
const THIN_CAP = 40;

export const completionWord = (pct: number, thin = 0): string => {
  let shown = pct;
  if (thin > 0) shown = Math.min(pct, THIN_CAP);
  const found = COMPLETION_WORD.find(([floor]) => shown >= floor);
  if (!found) return 'Just started';
  return found[1];
};

/** The arc this company walks, in its own order and words. */
const arcOf = (journey: OrbitJourney | null): PhaseCopy[] => {
  if (!journey || journey.phases.length === 0) return PHASES;
  return journey.phases.map((p) => ({
    n: p.n,
    key: p.key,
    screen: p.screen || 'none',
    title: p.title,
    purpose: p.purpose || '',
    builds: p.builds || [],
    unlocks: p.unlocks || '',
    action: p.action || 'Open this phase',
    hidden: Boolean(p.hidden),
  }));
};

/** The phases a reader can actually reach. The server still counts them all. */
export const shownPhases = (journey: OrbitJourney | null = null): PhaseCopy[] =>
  arcOf(journey).filter((p) => !p.hidden);

/** The intake the step at position n opens. */
export const screenOfPhase = (journey: OrbitJourney | null, n: number): PhaseScreen => {
  const found = arcOf(journey).find((p) => p.n === n);
  return found ? found.screen : 'none';
};

/** Where a given intake sits in this company's order, or 0 when it has none. */
export const phaseOfScreen = (journey: OrbitJourney | null, screen: PhaseScreen): number => {
  const found = arcOf(journey).find((p) => p.screen === screen);
  return found ? found.n : 0;
};

export const openPhaseLine = (doneMax: number, journey: OrbitJourney | null = null): string => {
  const shown = shownPhases(journey);
  if (doneMax >= shown.length) return `All ${shown.length} phases done`;
  return `Phase ${doneMax + 1} open`;
};

const progressOf = (key: PhaseScreen, overview: OrbitOverview | null): string => {
  if (!overview) return '';
  const counts = overview.counts;
  if (key === 'frame') {
    if (counts.open === 0) return `${counts.known} facts on file, nothing left to ask`;
    return `${counts.known} facts on file · ${counts.open} cards open`;
  }
  if (key === 'departments') {
    const depts = overview.dossiers.filter((d) => d.kind === 'department');
    const mapped = depts.filter((d) => d.ask === 0 && d.known > 0).length;
    return `${mapped} of ${depts.length} departments mapped`;
  }
  if (key === 'team') {
    const intake = overview.person_intake || { people: 0, done: 0 };
    if (intake.people === 0) return 'Nobody added yet';
    return `${intake.done} of ${intake.people} people done`;
  }
  if (key === 'tools') {
    const tools = overview.dossiers.find((d) => d.kind === 'tools');
    if (!tools) return '';
    return `${tools.known} of ${tools.fields_total} answered`;
  }
  if (key === 'report') {
    return `${overview.opportunities.open_count || overview.opportunities.items.length} opportunities forming`;
  }
  if (key.startsWith('intake:')) {
    const intake = overview.dossiers.find((d) => d.kind === 'intake' && d.key === key.slice('intake:'.length));
    if (!intake) return '';
    return `${intake.known} of ${intake.fields_total} answered`;
  }
  return '';
};

/** The phases on screen, each carrying the one state a reader can act on. */
export const phaseRows = (journey: OrbitJourney | null, overview: OrbitOverview | null): PhaseRow[] => {
  const marks = new Map((journey ? journey.phases : []).map((p) => [p.n, p]));
  const doneMax = journey ? journey.done_max : 0;
  return shownPhases(journey).map((p) => {
    const mark = marks.get(p.n);
    let state: OrbitPhaseState = 'locked';
    if (p.n <= doneMax) state = 'done';
    if (p.n === doneMax + 1) state = 'now';
    return {
      ...p,
      state: mark ? mark.state : state,
      doneBy: mark ? mark.done_by : '',
      doneAt: mark ? mark.done_at : null,
      progress: progressOf(p.screen, overview),
    };
  });
};
